<template>
  <div>
    <v-fab-transition v-if="isModification">
      <v-btn v-hotkey="'ctrl+s'" color="green" @click="save" icon size="large" class="fab-fixed-bottom-right">
        <v-icon size="large">mdi-content-save-outline</v-icon>
      </v-btn>
    </v-fab-transition>
    <v-container>
      <v-row>
        <v-col>
          <v-alert v-if="errorMessage" density="compact" variant="tonal" border="start" type="error">
            {{ errorMessage }}
          </v-alert>
          <v-alert v-if="isModification" density="compact" variant="tonal" border="start" type="warning">
            Modifications non sauvegardées. Sauvegardez (CTRL+S) avant de changer de section ou d'onglet.
          </v-alert>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-card variant="outlined">
            <v-card-title class="text-title-medium">Comparaison des calculs</v-card-title>
            <v-card-subtitle>
              Calculés avec les dernières données des stations (mises à jour à chaque extraction). Bande verte : débits
              recommandés.
            </v-card-subtitle>
            <v-card-text>
              <formula-comparison-chart
                :results="chartResults"
                :min-sug-level="toNumberOrNaN(section.min_sug_level)"
                :max-sug-level="toNumberOrNaN(section.max_sug_level)"
              ></formula-comparison-chart>
            </v-card-text>
            <!-- The public level is written by the gauge extraction (scrapeWaterFlow); this button doesn't wait for it -->
            <v-card-actions class="flex-wrap ga-2 px-4 pb-4">
              <v-btn
                color="primary"
                variant="flat"
                prepend-icon="mdi-refresh"
                :loading="recalculating"
                :disabled="!extrapolationId || isModification"
                @click="recalculate"
              >
                Recalculer maintenant
              </v-btn>
              <span class="text-body-small text-medium-emphasis">
                {{
                  isModification
                    ? "Sauvegardez d'abord vos modifications."
                    : "Met à jour le niveau public de la section avec le calcul principal sauvegardé et les dernières données des stations, sans attendre la prochaine extraction."
                }}
              </span>
            </v-card-actions>
            <v-alert
              v-if="recalculateMessage"
              :type="recalculateMessage.type"
              density="compact"
              variant="tonal"
              class="mx-4 mb-4"
            >
              {{ recalculateMessage.text }}
            </v-alert>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <p class="text-body-medium mb-3">
            Le calcul <strong>principal</strong> donne le niveau affiché sur le site. Les autres calculs cochés
            «&nbsp;graphique public&nbsp;» sont ajoutés en pointillés au graphique de la section, avec leur titre et leur
            précision. Chaque station utilise son facteur montant quand son débit monte (ou est stable) depuis 1 h, son
            facteur descendant quand il baisse.
          </p>

          <v-expansion-panels v-model="openPanels" multiple variant="accordion">
            <v-expansion-panel v-for="(formula, formulaIndex) in formulas" :key="formula.id" :value="formula.id">
              <v-expansion-panel-title>
                <div class="d-flex align-center flex-wrap ga-2 w-100">
                  <v-icon :color="formula.id === primaryFormulaId ? 'gold' : undefined">
                    {{ formula.id === primaryFormulaId ? "mdi-star" : "mdi-star-outline" }}
                  </v-icon>
                  <strong>{{ formula.title || "Sans titre" }}</strong>
                  <v-chip size="x-small" variant="tonal">Précision {{ accuracyLabel(formula.accuracy) }}</v-chip>
                  <v-chip v-if="formula.id === primaryFormulaId" size="x-small" color="gold-tint" variant="flat">
                    Principal
                  </v-chip>
                  <v-chip v-else-if="formula.show_on_chart" size="x-small" variant="outlined">Graphique public</v-chip>
                  <v-spacer></v-spacer>
                  <span class="text-body-small text-medium-emphasis mr-2">{{ currentFlowText(formula) }}</span>
                </div>
              </v-expansion-panel-title>

              <v-expansion-panel-text>
                <v-row dense>
                  <v-col cols="12" md="5">
                    <v-text-field v-model="formula.title" label="Titre" :counter="60" maxlength="60"></v-text-field>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-select
                      v-model="formula.accuracy"
                      :items="accuracyItems"
                      item-title="title"
                      item-value="value"
                      label="Précision"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" md="4" class="d-flex align-center flex-wrap ga-2">
                    <v-btn
                      size="small"
                      variant="tonal"
                      prepend-icon="mdi-star"
                      :disabled="formula.id === primaryFormulaId"
                      @click="primaryFormulaId = formula.id"
                    >
                      {{ formula.id === primaryFormulaId ? "Calcul principal" : "Rendre principal" }}
                    </v-btn>
                    <v-switch
                      v-if="formula.id !== primaryFormulaId"
                      v-model="formula.show_on_chart"
                      color="primary"
                      label="Graphique public"
                      hide-details
                      density="compact"
                    ></v-switch>
                  </v-col>
                </v-row>

                <div class="text-title-small mt-2 mb-1">Stations</div>
                <v-row v-for="(term, termIndex) in formula.terms" :key="termIndex" dense class="align-center">
                  <v-col cols="4" sm="2" md="1">
                    <!-- One button that switches between adding and subtracting the station -->
                    <v-btn
                      variant="tonal"
                      :color="term.operator === '-' ? 'error' : 'primary'"
                      class="text-title-large"
                      :title="term.operator === '-' ? 'Soustraite (cliquer pour ajouter)' : 'Ajoutée (cliquer pour soustraire)'"
                      :aria-label="term.operator === '-' ? 'Station soustraite' : 'Station ajoutée'"
                      @click="term.operator = term.operator === '-' ? '+' : '-'"
                    >
                      {{ term.operator === "-" ? "−" : "+" }}
                    </v-btn>
                  </v-col>
                  <v-col cols="8" sm="10" md="4">
                    <v-autocomplete
                      :model-value="term.gauge_id || null"
                      :items="processedGauges"
                      item-title="displayText"
                      item-value="id"
                      label="Station"
                      @update:model-value="setTermGauge(term, $event)"
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="6" sm="3" md="2">
                    <v-number-input
                      label="Facteur montant"
                      :model-value="toNumberOrNull(term.factor_up)"
                      :min="0"
                      :precision="null"
                      :step="0.05"
                      control-variant="hidden"
                      @update:model-value="term.factor_up = $event"
                    ></v-number-input>
                  </v-col>
                  <v-col cols="6" sm="3" md="2">
                    <v-number-input
                      label="Facteur descendant"
                      :model-value="toNumberOrNull(term.factor_down)"
                      :min="0"
                      :precision="null"
                      :step="0.05"
                      control-variant="hidden"
                      @update:model-value="term.factor_down = $event"
                    ></v-number-input>
                  </v-col>
                  <v-col cols="8" sm="4" md="2">
                    <v-number-input
                      label="Données d'il y a (h)"
                      :model-value="toNumberOrNull(term.lag_hours)"
                      :min="0"
                      :max="72"
                      :precision="null"
                      :step="0.25"
                      control-variant="hidden"
                      @update:model-value="term.lag_hours = $event"
                    ></v-number-input>
                  </v-col>
                  <v-col cols="4" sm="2" md="1" class="text-right">
                    <v-btn
                      icon="mdi-delete-outline"
                      variant="text"
                      size="small"
                      title="Retirer cette station"
                      :disabled="formula.terms.length <= 1"
                      @click="formula.terms.splice(termIndex, 1)"
                    ></v-btn>
                  </v-col>
                </v-row>

                <v-row dense class="align-center">
                  <v-col cols="12" sm="6" md="5">
                    <v-btn size="small" variant="text" prepend-icon="mdi-plus" @click="addTerm(formula)">
                      Ajouter une station
                    </v-btn>
                  </v-col>
                  <v-col cols="8" sm="4" md="3">
                    <v-number-input
                      label="Constante ajoutée (mcs)"
                      :model-value="toNumberOrNull(formula.offset)"
                      :precision="null"
                      :step="0.5"
                      control-variant="hidden"
                      @update:model-value="formula.offset = $event"
                    ></v-number-input>
                  </v-col>
                </v-row>

                <v-alert density="compact" variant="tonal" class="mt-2" :type="formulaError(formula) ? 'warning' : 'info'">
                  <div>
                    Débit =
                    <code
                      ><template v-for="(part, partIndex) in formulaParts(formula)" :key="partIndex"
                        ><gauge-term-tooltip
                          v-if="part.kind === 'gauge'"
                          :label="part.text"
                          :gauge-description="gaugeDescription(formula.terms[part.termIndex].gauge_id)"
                          :operator="formula.terms[part.termIndex].operator"
                          :lag-hours="Number(formula.terms[part.termIndex].lag_hours) || 0"
                          :explanation="termExplanation(formula, part.termIndex)"
                        ></gauge-term-tooltip
                        ><span v-else>{{ part.text }}</span></template
                      ></code
                    >
                  </div>
                  <div class="text-body-small">
                    Survolez un numéro de station pour voir ses dernières données et celles utilisées par le dernier
                    calcul.
                  </div>
                  <div v-if="formulaError(formula)">{{ formulaError(formula) }}</div>
                </v-alert>

                <div class="d-flex justify-end ga-2 mt-3">
                  <v-btn size="small" variant="text" prepend-icon="mdi-content-copy" @click="duplicateFormula(formula)">
                    Dupliquer
                  </v-btn>
                  <v-btn
                    size="small"
                    variant="text"
                    color="error"
                    prepend-icon="mdi-delete-outline"
                    :disabled="formulas.length <= 1"
                    @click="removeFormula(formulaIndex)"
                  >
                    Supprimer ce calcul
                  </v-btn>
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>

          <v-btn class="mt-4" color="primary" variant="tonal" prepend-icon="mdi-plus" @click="addFormula">
            Ajouter un calcul
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
  import SectionsService from "@/store/modules/sections";
  import { notify } from "@/services/Notification.service";
  import { functions } from "@/firebase";
  import { toNumberOrNull } from "@/utils/numbers";
  import FormulaComparisonChart from "@/components/pages/admin/FormulaComparisonChart.vue";
  import GaugeTermTooltip from "@/components/pages/admin/GaugeTermTooltip.vue";
  import {
    ACCURACY_LABELS,
    explainTermValue,
    formulaParts,
    evaluateFormula,
    getAccuracyLabel,
    getExtrapolationFormulas,
    getFormulaGaugeIds,
    normalizeFormula,
    parseGaugeReadings,
    parseReadingTime,
  } from "@/utils/flowFormulas";

  function newFormulaId() {
    return `f${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
  }

  function emptyTerm() {
    return { operator: "+", gauge_id: "", gauge_station_name: "", type: "", factor_up: 1, factor_down: 1, lag_hours: 0 };
  }

  // Station tab of the section editor: the flow formulas of the section (functions/includes/flowFormulas.js), a
  // preview of each one and a chart that compares them.
  export default {
    components: { FormulaComparisonChart, GaugeTermTooltip },
    props: {
      section: {
        type: Object,
        default: function () {
          return {};
        },
      },
      reloadData: {
        type: Boolean,
        default: false,
      },
    },
    emits: ["update"],
    data() {
      return {
        errorMessage: "",
        extrapolationId: null,
        formulas: [],
        primaryFormulaId: "",
        openPanels: [],
        savedSnapshot: "",
        recalculating: false,
        recalculateMessage: null,
        accuracyItems: Object.entries(ACCURACY_LABELS).map(([value, title]) => ({ value, title })),
      };
    },
    async created() {
      await this.getGauges();
      await this.getExtrapolation();
    },
    methods: {
      toNumberOrNull,
      formulaParts,
      accuracyLabel(accuracy) {
        return getAccuracyLabel(accuracy).toLowerCase();
      },
      toNumberOrNaN(value) {
        const number = toNumberOrNull(value);
        return number === null ? NaN : number;
      },
      snapshot() {
        return JSON.stringify({ formulas: this.formulas, primaryFormulaId: this.primaryFormulaId });
      },
      async getExtrapolation() {
        this.errorMessage = "";
        this.recalculateMessage = null;
        const extrapolations = await SectionsService.actions.getExtrapolationBySectionId(this.section.id);
        const extrapolation = extrapolations ? extrapolations[0] : null;
        const { formulas, primaryFormulaId } = getExtrapolationFormulas(extrapolation || {});

        this.extrapolationId = extrapolation ? extrapolation.id : null;
        this.formulas = formulas.length
          ? formulas
          : [normalizeFormula({ id: newFormulaId(), title: "Calcul principal", terms: [emptyTerm()] })];
        this.primaryFormulaId = primaryFormulaId || this.formulas[0].id;
        this.openPanels = [this.primaryFormulaId];
        this.savedSnapshot = this.snapshot();
      },
      async getGauges() {
        await this.$store.dispatch("bindGauges");
      },
      setTermGauge(term, gaugeId) {
        const gauge = this.gauges.find((item) => item.id === gaugeId);
        term.gauge_id = gauge ? gauge.id : "";
        term.gauge_station_name = gauge ? gauge.name : "";
        term.type = gauge ? gauge.type : "";
      },
      addTerm(formula) {
        formula.terms.push(emptyTerm());
      },
      addFormula() {
        const formula = normalizeFormula(
          { id: newFormulaId(), title: `Calcul ${this.formulas.length + 1}`, terms: [emptyTerm()] },
          this.formulas.length,
        );
        this.formulas.push(formula);
        this.openPanels = [...this.openPanels, formula.id];
      },
      duplicateFormula(formula) {
        const copy = {
          ...JSON.parse(JSON.stringify(formula)),
          id: newFormulaId(),
          title: `${formula.title} (copie)`.slice(0, 60),
          show_on_chart: false,
        };
        this.formulas.push(copy);
        this.openPanels = [...this.openPanels, copy.id];
      },
      removeFormula(index) {
        const [removed] = this.formulas.splice(index, 1);
        if (removed && removed.id === this.primaryFormulaId) {
          this.primaryFormulaId = this.formulas[0] ? this.formulas[0].id : "";
        }
      },
      formulaResult(formula) {
        return this.results[formula.id] || { level: [], missingGaugeIds: [] };
      },
      gaugeDescription(gaugeId) {
        const gauge = this.gauges.find((item) => item.id === gaugeId);
        return gauge && gauge.description ? gauge.description : "";
      },
      // Readings of a station and the ones used by the latest value of the formula (hover in the formula)
      termExplanation(formula, termIndex) {
        const term = formula.terms[termIndex];
        const latest = this.formulaResult(formula).level[0];
        const time = latest ? parseReadingTime(latest.date, latest.hour) : NaN;
        return explainTermValue(term, this.readingsByGauge[term.gauge_id] || [], time);
      },
      formulaError(formula) {
        if (!formula.terms.some((term) => term.gauge_id)) {
          return "Choisissez au moins une station.";
        }
        const result = this.formulaResult(formula);
        if (result.missingGaugeIds.length) {
          const names = result.missingGaugeIds.map((id) => {
            const gauge = this.gauges.find((item) => item.id === id);
            return gauge ? gauge.name : id;
          });
          return `Pas d'historique pour ${names.join(", ")} : ce calcul ne donne aucune valeur pour l'instant.`;
        }
        if (!result.level.length) {
          return "Les données des stations ne se chevauchent pas (vérifiez le délai) : aucune valeur.";
        }
        return "";
      },
      currentFlowText(formula) {
        const latest = this.formulaResult(formula).level[0];
        return latest ? `${latest.flow} mcs · ${latest.date} ${latest.hour}` : "Aucune valeur";
      },
      validate() {
        if (!this.formulas.length) {
          return "Ajoutez au moins un calcul.";
        }
        for (const formula of this.formulas) {
          if (!String(formula.title || "").trim()) {
            return "Chaque calcul doit avoir un titre.";
          }
          if (!formula.terms.length || formula.terms.some((term) => !term.gauge_id)) {
            return `« ${formula.title} » : choisissez une station pour chaque ligne.`;
          }
          const badFactor = formula.terms.some((term) =>
            [term.factor_up, term.factor_down].some((value) => toNumberOrNull(value) === null || value < 0),
          );
          if (badFactor) {
            return `« ${formula.title} » : les facteurs doivent être des nombres positifs.`;
          }
        }
        return "";
      },
      // Cloud Function recalculateSectionLevel (dataExtractFunctions.js): writes the public level now
      async recalculate() {
        this.recalculating = true;
        this.recalculateMessage = null;
        try {
          const response = await functions.httpsCallable("recalculateSectionLevel")({ sectionId: this.section.id });
          const result = (response && response.data) || {};
          if (result.updated) {
            const extra = result.chartFormulasCount
              ? ` ${result.chartFormulasCount} autre(s) calcul(s) sur le graphique public.`
              : "";
            this.recalculateMessage = {
              type: "success",
              text: `Niveau public mis à jour : ${result.currentLevel} mcs (donnée du ${result.levelLastUpdate}).${extra}`,
            };
            notify("Le niveau public de la section a été recalculé.");
          } else {
            const missing = (result.missingGaugeIds || []).map((id) => {
              const gauge = this.gauges.find((item) => item.id === id);
              return gauge ? gauge.name : id;
            });
            this.recalculateMessage = {
              type: "warning",
              text: missing.length
                ? `Rien n'a été mis à jour : pas de données pour ${missing.join(", ")}.`
                : "Rien n'a été mis à jour : le calcul principal ne donne aucune valeur avec les données actuelles.",
            };
          }
        } catch (error) {
          console.error(error);
          const code = String((error && error.code) || "").replace("functions/", "");
          const messages = {
            // The function's own French message ("Aucun calcul sauvegardé pour cette section.")
            "failed-precondition": error && error.message,
            "permission-denied": "Seuls les admins peuvent recalculer une section.",
            unauthenticated: "Votre session a expiré : reconnectez-vous.",
          };
          this.recalculateMessage = {
            type: "error",
            text:
              messages[code] ||
              "Le recalcul a échoué (la fonction est peut-être indisponible ou pas encore déployée). Réessayez plus tard.",
          };
        } finally {
          this.recalculating = false;
        }
      },
      async save() {
        const error = this.validate();
        if (error) {
          this.errorMessage = error;
          return;
        }
        try {
          this.extrapolationId = await SectionsService.actions.setExtrapolation(
            this.section.id,
            this.formulas,
            this.primaryFormulaId,
            this.extrapolationId,
          );
          this.savedSnapshot = this.snapshot();
          this.errorMessage = "";
          notify("Les calculs de la station ont été sauvegardés. Le niveau sera recalculé à la prochaine extraction.");
        } catch (saveError) {
          console.error(saveError);
          this.errorMessage = "Une erreur s'est produite lors de la sauvegarde des calculs, vérifiez et recommencez.";
        }
      },
    },
    computed: {
      gauges() {
        return this.$store.getters.gauges || [];
      },
      processedGauges() {
        return this.gauges.map((gauge) => ({
          ...gauge,
          displayText: `${gauge.type ? `[${gauge.type}] ` : ""}${gauge.name}${gauge.description ? ` - ${gauge.description}` : ""}`,
        }));
      },
      // Readings of the gauges used by the formulas only: data_history is large
      readingsByGauge() {
        const readings = {};
        getFormulaGaugeIds(this.formulas).forEach((gaugeId) => {
          const gauge = this.gauges.find((item) => item.id === gaugeId);
          readings[gaugeId] = gauge ? parseGaugeReadings(gauge.data_history) : [];
        });
        return readings;
      },
      results() {
        const results = {};
        this.formulas.forEach((formula) => {
          results[formula.id] = evaluateFormula(formula, this.readingsByGauge);
        });
        return results;
      },
      chartResults() {
        return this.formulas.map((formula) => ({
          id: formula.id,
          title: formula.title || "Sans titre",
          accuracy: formula.accuracy,
          primary: formula.id === this.primaryFormulaId,
          level: this.formulaResult(formula).level,
        }));
      },
      isModification() {
        return Boolean(this.savedSnapshot) && this.snapshot() !== this.savedSnapshot;
      },
    },
    watch: {
      reloadData() {
        if (this.reloadData) {
          this.getExtrapolation();
          this.$emit("update", false);
        }
      },
      formulas: {
        deep: true,
        handler() {
          this.errorMessage = "";
        },
      },
    },
  };
</script>
